import { useEffect } from 'react';

const px = (value) => parseFloat(value) || 0;

const verticalSpace = (el, includeClientHeight) => {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const margins = px(style.marginTop) + px(style.marginBottom);
  return includeClientHeight ? el.clientHeight + margins : margins;
};

/** clientHeight + marginTop + marginBottom of an element (0 when missing). */
export const outerHeight = (el) => verticalSpace(el, true);

/** marginTop + marginBottom of an element (0 when missing). */
export const verticalMargins = (el) => verticalSpace(el, false);

export const getById = (id) =>
  typeof document !== 'undefined' ? document.getElementById(id) : null;

export const getByTestId = (testId) =>
  typeof document !== 'undefined'
    ? document.querySelector(`[data-testid="${testId}"]`)
    : null;

/** Height of the journey progress bar above the current screen (0 when hidden). */
export const journeyProgressHeight = () =>
  outerHeight(getByTestId('journeyProgressBar'));

/**
 * Sizes an OLB scroll region's max-height to the form's remaining vertical
 * space so the cards scroll while the surrounding chrome (and the sticky
 * Continue footer / map header) stay visible. Recomputes on mount, on window
 * resize and when #formContentContainer resizes.
 *
 * When `enabled` is false (dynamic height on) no max-height is pinned and no
 * observers are attached — the content flows so the widget resizes to fit and
 * shrinks as cards collapse.
 *
 * @param {React.RefObject} targetRef - element whose maxHeight is set
 * @param {() => number} measure - returns the desired max-height in px
 * @param {Array} deps - recompute when these change (include `enabled`)
 * @param {boolean} enabled - only pin a max-height when true (dynamic height off)
 */
export const useOlbScrollMaxHeight = (targetRef, measure, deps, enabled) => {
  useEffect(() => {
    if (!enabled) {
      const el = targetRef.current;
      if (el) el.style.maxHeight = '';
      return undefined;
    }

    const apply = () => {
      const el = targetRef.current;
      if (!el) return;
      const height = measure();
      el.style.maxHeight = height > 0 ? `${height}px` : '';
    };

    apply();
    // Re-measure after layout settles (fonts, the fade transition, etc.).
    const frame = requestAnimationFrame(apply);
    window.addEventListener('resize', apply);

    const formContent = getById('formContentContainer');
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (observer && formContent) observer.observe(formContent);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', apply);
      if (observer) observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
